import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import moment from 'moment-timezone';
import DataApi from '../../api/DataApi';
import { getUserPreferences } from '../../utils/formatUtils';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import { getMultiCurrencySettings } from '../../utils/multiCurrencySettings';
import LookupLayout from './LookupLayout';
import MultiSelectDropdown from './MultiSelectDropdown1';
import CustomRichTextEditor from '../common/CustomRichTextEditor';
import { hasPackage } from '../../utils/entitlements';
import { theme } from '../../theme/theme.js';
import { getUserFieldPermissionsByObject, getFlsEditDeniedLowerSet } from '../../utils/permissionCache';
import SystemSettingsApi from '../../api/SystemSettingsApi';
import CurrencyApi from '../../api/CurrencyApi';

function parseBoolean(value) {
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  return false;
}

/** Same as EditLayout: currency picklist options for layout currency field. */
async function loadCurrencyOptionsForLayout() {
  const settingsApi = new SystemSettingsApi();
  const currencyApi = new CurrencyApi();
  let companyCurrency = '';
  let multiCurrencyEnabled = false;
  try {
    const settingsRes = await settingsApi.fetchSettings();
    const settingsData = settingsRes?.data;
    if (settingsData?.success && Array.isArray(settingsData.data)) {
      const companyItem = settingsData.data.find((s) => s && s.key === 'company_currency');
      companyCurrency = companyItem && companyItem.value ? String(companyItem.value).trim() : '';
      const multiItem = settingsData.data.find((s) => s && s.key === 'multi_currency_enabled');
      multiCurrencyEnabled = multiItem ? parseBoolean(multiItem.value) : false;
    }
  } catch {
    /* ignore */
  }
  const options = [];
  let defaultCode = companyCurrency || '';
  if (!multiCurrencyEnabled) {
    if (companyCurrency) {
      options.push({ key: companyCurrency, value: companyCurrency, is_default: true });
    }
    return { options, defaultCode };
  }
  try {
    const currencyRes = await currencyApi.fetchAll();
    const list = currencyRes?.data?.data;
    if (!Array.isArray(list) || list.length === 0) {
      if (companyCurrency) {
        options.push({ key: companyCurrency, value: companyCurrency, is_default: true });
      }
      return { options, defaultCode };
    }
    const defaultRow = list.find((r) => r.is_default === true);
    defaultCode =
      defaultRow && defaultRow.currency_code
        ? String(defaultRow.currency_code).trim()
        : companyCurrency || (list[0] && list[0].currency_code) || '';
    list.forEach((row) => {
      const code = row.currency_code ? String(row.currency_code).trim() : '';
      if (!code) return;
      const label = [row.symbol, row.currency_code, row.currency_name].filter(Boolean).join(' - ') || code;
      options.push({
        key: code,
        value: label,
        is_default: !!row.is_default,
      });
    });
    return { options, defaultCode };
  } catch {
    if (companyCurrency) {
      options.push({ key: companyCurrency, value: companyCurrency, is_default: true });
    }
    return { options, defaultCode };
  }
}

/**
 * Root → … → immediate parent for a dependent picklist (walk controlling chain).
 */
export function buildPicklistAncestorChain(picklistDependencies, targetFieldName) {
  if (!picklistDependencies?.length || !targetFieldName) return [];
  const chain = [];
  let child = String(targetFieldName);
  const seen = new Set();
  for (let depth = 0; depth < 32; depth += 1) {
    const dep = picklistDependencies.find(
      (d) => d.dependent_field_details?.name === child
    );
    if (!dep) break;
    const parent = dep.controlling_field_details?.name;
    if (!parent || seen.has(parent)) break;
    seen.add(parent);
    chain.unshift(parent);
    child = parent;
  }
  return chain;
}

/**
 * Walk up dependency edges until the top-level controlling field (not a dependent of another).
 */
function findPicklistDependencyRootName(picklistDependencies, fieldName) {
  if (!picklistDependencies?.length || !fieldName) return null;
  let current = String(fieldName);
  const seen = new Set();
  for (let d = 0; d < 32; d += 1) {
    if (seen.has(current)) return current;
    seen.add(current);
    const dep = picklistDependencies.find(
      (x) => x.dependent_field_details?.name === current
    );
    if (!dep) return current;
    const p = dep.controlling_field_details?.name;
    if (!p) return current;
    current = p;
  }
  return current;
}

/**
 * From root, follow controlling → dependent to build pick1, pick2, …, pickN in order.
 */
function buildPicklistChainFromRootDownward(picklistDependencies, rootFieldName) {
  if (!rootFieldName) return [];
  const ordered = [String(rootFieldName)];
  let current = String(rootFieldName);
  const seen = new Set(ordered);
  for (let d = 0; d < 32; d += 1) {
    const nextDep = picklistDependencies.find(
      (x) => x.controlling_field_details?.name === current
    );
    if (!nextDep) break;
    const next = nextDep.dependent_field_details?.name;
    if (!next || seen.has(next)) break;
    seen.add(next);
    ordered.push(next);
    current = next;
  }
  return ordered;
}

/**
 * For pick1 → pick2 → pick3, opening quick-edit on any of these fields should show the
 * same full list [pick1, pick2, pick3] — not just ancestors+target (which missed the root
 * when opening pick1, or the tail when opening pick2).
 */
export function buildFullDependentPicklistFieldOrder(
  picklistDependencies,
  fieldName
) {
  if (!picklistDependencies?.length || !fieldName) return null;
  const root = findPicklistDependencyRootName(picklistDependencies, fieldName);
  if (root == null) return null;
  const full = buildPicklistChainFromRootDownward(picklistDependencies, root);
  if (full.length < 2) return null;
  if (!full.includes(String(fieldName))) return null;
  return full;
}

/**
 * Filter dependent picklist options from full list (same rules as EditLayout#getDependentPicklistValues).
 */
function filterDependentPicklistOptions(
  picklistDependencies,
  picklistValuesMap,
  controllingFieldName,
  controllingValue,
  dependentFieldName
) {
  if (
    !picklistDependencies?.length ||
    controllingValue === '' ||
    controllingValue === null ||
    controllingValue === undefined
  ) {
    return [];
  }
  const dependency = picklistDependencies.find(
    (dep) =>
      dep.controlling_field_details?.name === controllingFieldName &&
      dep.dependent_field_details?.name === dependentFieldName
  );
  if (!dependency?.value_dependencies?.length) {
    return [];
  }
  const ctrlStr =
    typeof controllingValue === 'boolean'
      ? String(controllingValue)
      : String(controllingValue);
  let dependentValueKeys = [];
  if (ctrlStr.includes(';')) {
    const controllingValues = ctrlStr.split(';').filter((v) => v.trim() !== '');
    const allDependentKeys = new Set();
    controllingValues.forEach((controllingVal) => {
      dependency.value_dependencies
        .filter((vd) => vd.controlling_value_key === controllingVal.trim())
        .forEach((vd) => allDependentKeys.add(vd.dependent_value_key));
    });
    dependentValueKeys = Array.from(allDependentKeys);
  } else {
    dependentValueKeys = dependency.value_dependencies
      .filter((vd) => vd.controlling_value_key === ctrlStr)
      .map((vd) => vd.dependent_value_key);
  }
  if (dependentValueKeys.length === 0) return [];
  const fullList = picklistValuesMap[dependentFieldName] || [];
  return fullList.filter((pv) => {
    const pvKey = pv.key || pv.value_key;
    return dependentValueKeys.includes(pvKey);
  });
}

/** After a value changes at ordered[fromIndex], clear or trim invalid dependent values below. */
function recomputePicklistHierarchyDownstream(
  ordered,
  values,
  fromIndex,
  picklistDependencies,
  picklistValuesMap,
  allFields
) {
  let next = { ...values };
  for (let i = fromIndex + 1; i < ordered.length; i += 1) {
    const childName = ordered[i];
    const childField = findFieldByName(allFields, childName);
    const dep = picklistDependencies.find(
      (d) => d.dependent_field_details?.name === childName
    );
    if (!dep) continue;
    const pName = dep.controlling_field_details?.name;
    const pVal = next[pName];
    let opts = [];
    if (pVal !== '' && pVal !== null && pVal !== undefined) {
      opts = filterDependentPicklistOptions(
        picklistDependencies,
        picklistValuesMap,
        pName,
        pVal,
        childName
      );
    }
    const keys = opts.map((o) => o.key || o.value_key);
    const cur = next[childName];
    const ct = String(childField?.type || '').toLowerCase();
    if (ct === 'multipicklist') {
      const parts = String(cur || '')
        .split(';')
        .filter((x) => x.trim() !== '');
      const valid = parts.filter((p) => keys.includes(String(p).trim()));
      next[childName] = valid.join(';');
    } else if (cur != null && cur !== '' && !keys.includes(String(cur).trim())) {
      next[childName] = '';
    }
  }
  return next;
}

async function buildPicklistValuesMapFromAllFields(allFields) {
  const allPicklistFields = allFields.filter(
    (f) => f.type === 'picklist' || f.type === 'multipicklist'
  );
  let currencyOptionsForForm = null;
  if (allPicklistFields.some((f) => String(f.fieldName || '').toLowerCase() === 'currency')) {
    try {
      currencyOptionsForForm = await loadCurrencyOptionsForLayout();
    } catch {
      /* ignore */
    }
  }
  const picklistValuesMap = {};
  for (const field of allPicklistFields) {
    if (String(field.fieldName || '').toLowerCase() === 'currency' && currencyOptionsForForm) {
      picklistValuesMap[field.fieldName] = currencyOptionsForForm.options;
      continue;
    }
    if (field.picklistValues && Array.isArray(field.picklistValues)) {
      picklistValuesMap[field.fieldName] = field.picklistValues;
    } else if (field.picklistValues && typeof field.picklistValues === 'string') {
      try {
        const parsed = JSON.parse(field.picklistValues);
        picklistValuesMap[field.fieldName] = Array.isArray(parsed) ? parsed : [];
      } catch {
        picklistValuesMap[field.fieldName] = [];
      }
    } else {
      picklistValuesMap[field.fieldName] = [];
    }
  }
  return picklistValuesMap;
}

function normalizePicklistOptions(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((opt) => ({
    key: opt.key || opt.value_key,
    value: opt.value || opt.value_label,
  }));
}

function editResponseToSections(data) {
  if (!data || typeof data !== 'object') return { sections: [], hidden: [] };
  let sections = [];
  if (Array.isArray(data)) {
    sections = data;
  } else if (Array.isArray(data.details)) {
    sections = data.details;
  }
  const hiddenFromApi =
    !Array.isArray(data) && Array.isArray(data.otherHiddenFields)
      ? data.otherHiddenFields
      : [];
  const sectionNames = sections.map((s) => s.sectionName);
  const unique = [...new Set(sectionNames)];
  if (sectionNames.length !== unique.length) {
    const seen = new Set();
    sections = sections.filter((section) => {
      if (seen.has(section.sectionName)) return false;
      seen.add(section.sectionName);
      return true;
    });
  }
  const flatFields = sections.flatMap((section) => section.fields || []);
  const seenFieldNames = new Set();
  const allFields = flatFields.filter((f) => {
    const name = f?.fieldName;
    if (!name) return false;
    if (seenFieldNames.has(name)) return false;
    seenFieldNames.add(name);
    return true;
  });
  hiddenFromApi.forEach((f) => {
    const name = f?.fieldName;
    if (!name || seenFieldNames.has(name)) return;
    seenFieldNames.add(name);
    allFields.push(f);
  });
  return { sections, allFields, header: data.header, raw: data };
}

function buildFormDefaultForField(f) {
  if (!f) return '';
  if (f.value === undefined || f.value === null || f.value === '') {
    if (f.type === 'boolean' || f.type === 'checkbox') return false;
    return '';
  }
  if (f.type === 'datetime' && f.value) {
    const prefs = getUserPreferences();
    const userTimezone = prefs.time_zone || 'UTC';
    const momentDate = moment(f.value).tz(userTimezone);
    return `${momentDate.format('YYYY-MM-DD')}T${momentDate.format('HH:mm')}`;
  }
  if (f.type === 'date' && f.value) {
    const prefs = getUserPreferences();
    const userTimezone = prefs.time_zone || 'UTC';
    return moment(f.value).tz(userTimezone).format('YYYY-MM-DD');
  }
  if (f.type === 'time' && f.value) {
    let timeValue = f.value;
    if (typeof timeValue === 'string' && (timeValue.includes('T') || timeValue.includes(' '))) {
      const d = new Date(timeValue);
      timeValue = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    } else if (typeof timeValue === 'string' && timeValue.includes(':')) {
      const parts = timeValue.split(':');
      timeValue = `${parts[0]}:${parts[1]}`;
    }
    return timeValue;
  }
  if (f.type === 'boolean' || f.type === 'checkbox') {
    return Boolean(f.value);
  }
  if (f.type === 'password') {
    return '';
  }
  return f.value ?? '';
}

function buildFormDefaultsMap(allFields) {
  const map = {};
  const labels = {};
  allFields.forEach((f) => {
    if (!f?.fieldName) return;
    map[f.fieldName] = buildFormDefaultForField(f);
    if (f.type === 'lookup') {
      labels[f.fieldName] = f.displayValue || '';
    }
  });
  return { map, labels };
}

/** Values for {{field}} resolution in lookup filters (string ids / scalars). */
function buildLookupFilterFieldValues(formMap) {
  const out = {};
  if (!formMap || typeof formMap !== 'object') return out;
  Object.keys(formMap).forEach((k) => {
    const v = formMap[k];
    if (v === null || v === undefined) {
      out[k] = '';
    } else if (typeof v === 'boolean') {
      out[k] = v ? 'true' : 'false';
    } else {
      out[k] = String(v);
    }
  });
  return out;
}

function sanitizeSingleFieldPayload(field, rawDraft) {
  const name = field.fieldName;
  const t = String(field.type || '').toLowerCase();
  const value = rawDraft;

  if (t === 'datetime' && value) {
    const prefs = getUserPreferences();
    const userTimezone = prefs.time_zone || 'UTC';
    return { [name]: moment.tz(value, userTimezone).format() };
  }
  if (t === 'date' && value) {
    const prefs = getUserPreferences();
    const userTimezone = prefs.time_zone || 'UTC';
    return { [name]: moment.tz(value, userTimezone).startOf('day').format() };
  }
  if (t === 'time' && value) {
    let timeValue = value;
    if (typeof timeValue === 'string' && timeValue.includes(':')) {
      const parts = timeValue.split(':');
      if (parts.length === 2) {
        timeValue = `${timeValue}:00`;
      }
    }
    return { [name]: timeValue };
  }
  if (t === 'number' || t === 'decimal' || t === 'currency') {
    if (value === '' || value == null) {
      return { [name]: null };
    }
    return { [name]: Number(value) };
  }
  if (t === 'boolean' || t === 'checkbox') {
    return { [name]: Boolean(value) };
  }
  if (t === 'password') {
    return { [name]: value || '' };
  }
  return { [name]: value || null };
}

function findFieldByName(allFields, fieldName) {
  const want = String(fieldName || '').trim().toLowerCase();
  return allFields.find((f) => String(f.fieldName || '').trim().toLowerCase() === want) || null;
}

const TASK_FULL_EDIT_FIELDS = new Set([
  'related_object',
  'related_object_email_field',
  'related_object_phone_field',
]);

/**
 * Modal: edit one field loaded from `object/edit/:object/:id` (same metadata as full edit).
 */
export default function DetailSingleFieldEditModal({
  show,
  onHide,
  objectName,
  recordId,
  fieldName,
  onSaved,
  onOpenFullEdit,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [field, setField] = useState(null);
  const [allFieldsList, setAllFieldsList] = useState([]);
  const [draft, setDraft] = useState('');
  const [lookupLabel, setLookupLabel] = useState('');
  const [formDefaultsMap, setFormDefaultsMap] = useState({});
  const [flsBlocked, setFlsBlocked] = useState(false);
  const [picklistDependencies, setPicklistDependencies] = useState([]);
  const [picklistValuesMap, setPicklistValuesMap] = useState({});
  /** Ordered root → parent → … → target when editing a dependent picklist */
  const [picklistHierarchyOrder, setPicklistHierarchyOrder] = useState(null);
  const [hierarchyValues, setHierarchyValues] = useState({});

  const reset = useCallback(() => {
    setLoading(false);
    setSaving(false);
    setLoadError(null);
    setSaveError(null);
    setField(null);
    setAllFieldsList([]);
    setDraft('');
    setLookupLabel('');
    setFormDefaultsMap({});
    setFlsBlocked(false);
    setPicklistDependencies([]);
    setPicklistValuesMap({});
    setPicklistHierarchyOrder(null);
    setHierarchyValues({});
  }, []);

  useEffect(() => {
    if (!show) {
      reset();
      return;
    }
    if (!objectName || !recordId || !fieldName) {
      setLoadError('Missing record or field.');
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      setSaveError(null);
      setField(null);
      setFlsBlocked(false);
      try {
        const dataApi = new DataApi(`object/edit/${objectName}`);
        const response = await dataApi.fetchById(String(recordId));
        const data = response?.data?.data;
        if (cancelled) return;

        if (typeof data === 'string') {
          setLoadError(data || 'Could not load field.');
          setLoading(false);
          return;
        }
        if (!data || Object.keys(data).length === 0) {
          setLoadError('No record found.');
          setLoading(false);
          return;
        }

        const { allFields } = editResponseToSections(data);
        const found = findFieldByName(allFields, fieldName);
        if (!found) {
          setLoadError('This field is not on the edit layout for this record.');
          setLoading(false);
          return;
        }

        let flsDenied = new Set();
        try {
          const flsByObj = await getUserFieldPermissionsByObject();
          flsDenied = getFlsEditDeniedLowerSet(flsByObj, objectName);
        } catch {
          /* non-fatal */
        }
        if (flsDenied.has(String(found.fieldName).trim().toLowerCase())) {
          setFlsBlocked(true);
          setField(found);
          setLoading(false);
          return;
        }

        if (String(objectName).toLowerCase() === 'task' && TASK_FULL_EDIT_FIELDS.has(String(found.fieldName).toLowerCase())) {
          setLoadError('This field must be edited from the full record form.');
          setLoading(false);
          return;
        }

        const { map } = buildFormDefaultsMap(allFields);
        const initial = buildFormDefaultForField(found);
        let label = '';
        if (found.type === 'lookup') {
          label = found.displayValue || '';
        }

        const pickMap = await buildPicklistValuesMapFromAllFields(allFields);
        let dependencies = [];
        try {
          const dependenciesApi = new DataApi(
            `picklist/field-dependencies/object/${objectName}`
          );
          const dependenciesResponse = await dependenciesApi.fetchAll();
          if (dependenciesResponse?.data?.success && Array.isArray(dependenciesResponse.data.data)) {
            dependencies = dependenciesResponse.data.data;
          }
        } catch {
          /* non-fatal */
        }

        const ftLow = String(found.type || '').toLowerCase();
        let hierarchyOrder = null;
        let hierarchyInit = {};
        if ((ftLow === 'picklist' || ftLow === 'multipicklist') && dependencies.length > 0) {
          const ordered = buildFullDependentPicklistFieldOrder(dependencies, found.fieldName);
          if (ordered && ordered.length > 0) {
            const missing = ordered.filter((fn) => !findFieldByName(allFields, fn));
            if (missing.length > 0) {
              setLoadError(
                `Dependent picklist needs field(s) on this layout: ${missing.join(', ')}. Use full record edit.`
              );
              setAllFieldsList(allFields);
              setField(found);
              setFormDefaultsMap(map);
              setPicklistValuesMap(pickMap);
              setPicklistDependencies(dependencies);
              setPicklistHierarchyOrder(null);
              setHierarchyValues({});
              setDraft(initial);
              setLookupLabel(label);
              setLoading(false);
              return;
            }
            hierarchyOrder = ordered;
            ordered.forEach((fn) => {
              const v = map[fn];
              hierarchyInit[fn] = v === undefined || v === null ? '' : v;
            });
            let hv = { ...hierarchyInit };
            for (let j = 0; j < ordered.length - 1; j += 1) {
              hv = recomputePicklistHierarchyDownstream(
                ordered,
                hv,
                j,
                dependencies,
                pickMap,
                allFields
              );
            }
            hierarchyInit = hv;
          }
        }

        setAllFieldsList(allFields);
        setField(found);
        setFormDefaultsMap(map);
        setPicklistValuesMap(pickMap);
        setPicklistDependencies(dependencies);
        setPicklistHierarchyOrder(hierarchyOrder);
        setHierarchyValues(hierarchyInit);
        setDraft(initial);
        setLookupLabel(label);
      } catch (e) {
        if (!cancelled) {
          setLoadError(getApiErrorMessage(e, 'Failed to load field.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show, objectName, recordId, fieldName, reset]);

  const lookupFilterValues = useMemo(
    () => buildLookupFilterFieldValues({ ...formDefaultsMap, ...hierarchyValues }),
    [formDefaultsMap, hierarchyValues]
  );

  const updateHierarchyPicklist = useCallback(
    (fieldApiName, newValue) => {
      setHierarchyValues((prev) => {
        const ordered = picklistHierarchyOrder;
        if (!ordered?.length) return { ...prev, [fieldApiName]: newValue };
        const idx = ordered.indexOf(fieldApiName);
        if (idx < 0) return { ...prev, [fieldApiName]: newValue };
        const next = { ...prev, [fieldApiName]: newValue };
        return recomputePicklistHierarchyDownstream(
          ordered,
          next,
          idx,
          picklistDependencies,
          picklistValuesMap,
          allFieldsList
        );
      });
    },
    [picklistHierarchyOrder, allFieldsList, picklistDependencies, picklistValuesMap]
  );

  const renderPicklistHierarchy = () => {
    if (!picklistHierarchyOrder?.length || !field) return null;
    const commonStyle = {
      fontFamily: theme.typography.fontFamily.primary,
      fontSize: theme.typography.fontSize.lg,
    };
    return (
      <div className="dependent-picklist-hierarchy">
        <p className="text-muted small mb-3">
          This shows the full controlling chain for this picklist. Only the top-level field lists every
          option; dependent fields list values allowed for the current parent selection. Changing a
          parent may clear child values that are no longer valid.
        </p>
        {picklistHierarchyOrder.map((fname, idx) => {
          const fmeta = findFieldByName(allFieldsList, fname);
          if (!fmeta) return null;
          let opts;
          if (idx === 0) {
            const rawOpts = picklistValuesMap[fname] || [];
            opts = normalizePicklistOptions(rawOpts);
          } else {
            const parentName = picklistHierarchyOrder[idx - 1];
            const parentVal = hierarchyValues[parentName];
            opts = normalizePicklistOptions(
              filterDependentPicklistOptions(
                picklistDependencies,
                picklistValuesMap,
                parentName,
                parentVal,
                fname
              )
            );
          }
          if (
            String(objectName).toLowerCase() === 'task' &&
            String(fname).toLowerCase() === 'notification_type' &&
            !hasPackage('watconnect')
          ) {
            opts = opts.filter((o) => String(o.key || '').toLowerCase() !== 'whatsapp');
          }
          const t = String(fmeta.type || '').toLowerCase();
          const val = hierarchyValues[fname] ?? '';
          const picklistEmptyHint = !opts.length ? 'No values available' : 'Select...';
          const parentName = idx > 0 ? picklistHierarchyOrder[idx - 1] : null;
          const parentMeta = parentName ? findFieldByName(allFieldsList, parentName) : null;
          const parentHasSelection =
            parentName &&
            hierarchyValues[parentName] !== '' &&
            hierarchyValues[parentName] != null;
          return (
            <Form.Group key={fname} className="mb-3">
              <Form.Label className="fw-medium">{fmeta.label || fname}</Form.Label>
              {t === 'multipicklist' ? (
                <MultiSelectDropdown
                  selectedValues={val ? String(val).split(';').filter(Boolean) : []}
                  picklistValues={opts}
                  onChange={(sel) => updateHierarchyPicklist(fname, sel.join(';'))}
                  isRequired={fmeta.isRequired}
                  placeholder={picklistEmptyHint}
                  disabled={!opts.length}
                />
              ) : (
                <Form.Select
                  value={val || ''}
                  onChange={(e) => updateHierarchyPicklist(fname, e.target.value)}
                  style={commonStyle}
                  disabled={!opts.length}
                >
                  <option value="">-- Select --</option>
                  {opts.map((opt) => (
                    <option key={String(opt.key)} value={String(opt.key)}>
                      {opt.value}
                    </option>
                  ))}
                </Form.Select>
              )}
              {idx > 0 && parentHasSelection && !opts.length ? (
                <Form.Text className="text-muted fst-italic">
                  No {fmeta.label || fname} values available for selected{' '}
                  {parentMeta?.label || parentName}.
                </Form.Text>
              ) : null}
            </Form.Group>
          );
        })}
      </div>
    );
  };

  const handleSave = async () => {
    if (!field || !objectName || !recordId || flsBlocked) return;
    const ft = String(field.type || '').toLowerCase();
    if (ft === 'password' && (!draft || String(draft).trim() === '')) {
      setSaveError('Enter a new password or cancel.');
      return;
    }
    if (picklistHierarchyOrder?.length) {
      const missingReq = picklistHierarchyOrder.filter((fn) => {
        const fm = findFieldByName(allFieldsList, fn);
        if (!fm?.isRequired) return false;
        const v = hierarchyValues[fn];
        return v === '' || v === null || v === undefined;
      });
      if (missingReq.length > 0) {
        setSaveError('Please fill all required fields in the hierarchy.');
        return;
      }
    }
    setSaving(true);
    setSaveError(null);
    try {
      let payload;
      if (picklistHierarchyOrder?.length) {
        payload = {};
        picklistHierarchyOrder.forEach((fn) => {
          const raw = hierarchyValues[fn];
          payload[fn] = raw === '' || raw == null ? null : raw;
        });
      } else {
        payload = sanitizeSingleFieldPayload(field, draft);
      }

      const hasCurrencyOnObject = allFieldsList.some(
        (f) =>
          f.type === 'currency' || String(f.fieldName || '').toLowerCase() === 'currency'
      );
      if (hasCurrencyOnObject) {
        try {
          const { multiCurrencyEnabled, companyCurrency } = await getMultiCurrencySettings();
          if (!multiCurrencyEnabled && companyCurrency) {
            payload = { ...payload, currency: companyCurrency };
            delete payload.exchange_rate;
          }
        } catch {
          /* ignore */
        }
      }

      const dataApi = new DataApi(`object/${objectName}`);
      await dataApi.update(payload, String(recordId));
      if (onSaved) onSaved();
      onHide();
    } catch (e) {
      setSaveError(getApiErrorMessage(e, 'Save failed.'));
    } finally {
      setSaving(false);
    }
  };

  const renderControl = () => {
    if (!field) return null;
    const t = String(field.type || 'text').toLowerCase();
    const commonStyle = {
      fontFamily: theme.typography.fontFamily.primary,
      fontSize: theme.typography.fontSize.lg,
    };

    if (field.isReadonly === true) {
      return <p className="text-muted mb-0">This field is read-only.</p>;
    }

    if ((t === 'picklist' || t === 'multipicklist') && picklistHierarchyOrder?.length) {
      return renderPicklistHierarchy();
    }

    switch (t) {
      case 'picklist': {
        let opts = normalizePicklistOptions(
          picklistValuesMap[field.fieldName] || field.picklistValues
        );
        if (
          String(objectName).toLowerCase() === 'task' &&
          String(field.fieldName).toLowerCase() === 'notification_type' &&
          !hasPackage('watconnect')
        ) {
          opts = opts.filter((o) => String(o.key || '').toLowerCase() !== 'whatsapp');
        }
        return (
          <Form.Select
            value={draft || ''}
            onChange={(e) => setDraft(e.target.value)}
            style={commonStyle}
          >
            <option value="">-- Select --</option>
            {opts.map((opt) => (
              <option key={String(opt.key)} value={String(opt.key)}>
                {opt.value}
              </option>
            ))}
          </Form.Select>
        );
      }
      case 'multipicklist': {
        let opts = normalizePicklistOptions(
          picklistValuesMap[field.fieldName] || field.picklistValues
        );
        if (
          String(objectName).toLowerCase() === 'task' &&
          String(field.fieldName).toLowerCase() === 'notification_type' &&
          !hasPackage('watconnect')
        ) {
          opts = opts.filter((o) => String(o.key || '').toLowerCase() !== 'whatsapp');
        }
        const selected = draft ? String(draft).split(';').filter(Boolean) : [];
        return (
          <MultiSelectDropdown
            selectedValues={selected}
            picklistValues={opts}
            onChange={(sel) => setDraft(sel.join(';'))}
            isRequired={field.isRequired}
            placeholder={opts.length ? 'Select...' : 'No values available'}
            disabled={!opts.length}
          />
        );
      }
      case 'lookup':
        return (
          <LookupLayout
            objectName={field.referenceObject}
            searchFields={field.lookupSearchFields || field.lookup_search_fields || 'name'}
            displayFields={field.displayFields || field.display_fields || 'name'}
            value={draft}
            defaultLabel={lookupLabel}
            onChange={(id, label) => {
              setDraft(id || '');
              setLookupLabel(label || '');
            }}
            disabled={false}
            lookupFilter={field.lookupFilter || field.lookup_filter}
            lookupFilterFieldValues={lookupFilterValues}
            scopeRecordviewToObjectName={objectName}
          />
        );
      case 'boolean':
      case 'checkbox':
        return (
          <Form.Check
            type="checkbox"
            checked={Boolean(draft)}
            onChange={(e) => setDraft(e.target.checked)}
            label=" "
            aria-label={field.label || field.fieldName}
          />
        );
      case 'date':
        return (
          <Form.Control
            type="date"
            value={draft || ''}
            onChange={(e) => setDraft(e.target.value)}
            style={commonStyle}
          />
        );
      case 'datetime':
        return (
          <Form.Control
            type="datetime-local"
            value={draft || ''}
            onChange={(e) => setDraft(e.target.value)}
            style={commonStyle}
          />
        );
      case 'time':
        return (
          <Form.Control
            type="time"
            value={draft || ''}
            onChange={(e) => setDraft(e.target.value)}
            style={commonStyle}
          />
        );
      case 'number':
      case 'decimal':
      case 'currency':
        return (
          <Form.Control
            type="number"
            step={t === 'currency' || t === 'decimal' ? 'any' : '1'}
            value={draft === '' || draft == null ? '' : draft}
            onChange={(e) => setDraft(e.target.value)}
            style={commonStyle}
          />
        );
      case 'password':
        return (
          <Form.Control
            type="password"
            autoComplete="new-password"
            value={draft || ''}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Leave blank to keep current"
            style={commonStyle}
          />
        );
      case 'richtext':
        return (
          <CustomRichTextEditor
            value={draft || ''}
            onChange={setDraft}
            placeholder={field.label}
            height="220px"
            toolbarConfig="standard"
          />
        );
      case 'textarea':
        return (
          <Form.Control
            as="textarea"
            rows={4}
            value={draft || ''}
            onChange={(e) => setDraft(e.target.value)}
            style={commonStyle}
          />
        );
      default:
        return (
          <Form.Control
            type={t === 'email' ? 'email' : t === 'phone' ? 'tel' : 'text'}
            value={draft || ''}
            onChange={(e) => setDraft(e.target.value)}
            style={commonStyle}
          />
        );
    }
  };

  const title = field ? `Edit ${field.label || field.fieldName}` : 'Edit field';

  const modalSize =
    picklistHierarchyOrder && picklistHierarchyOrder.length > 2 ? 'lg' : 'md';

  return (
    <Modal show={show} onHide={onHide} centered size={modalSize} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.1rem' }}>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" role="status" />
          </div>
        ) : loadError ? (
          <p className="text-danger mb-2">{loadError}</p>
        ) : flsBlocked ? (
          <p className="text-muted mb-0">You do not have permission to edit this field.</p>
        ) : (
          <>
            {saveError ? <p className="text-danger small mb-2">{saveError}</p> : null}
            {renderControl()}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <div>
          {onOpenFullEdit && !loading ? (
            <Button variant="outline-secondary" size="sm" onClick={onOpenFullEdit} disabled={saving}>
              Full record edit
            </Button>
          ) : null}
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || loading || !!loadError || flsBlocked || !field || field.isReadonly === true}
          >
            {saving ? <Spinner animation="border" size="sm" /> : 'Save'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export function detailFieldSupportsQuickEdit(field) {
  if (!field?.fieldName) return false;
  if (field.isReadonly === true) return false;
  const t = String(field.type || 'text').toLowerCase();
  const isPicklist = t === 'picklist' || t === 'multipicklist';
  // Controlling / stage fields are often both path fields and picklists. Blocking all path fields
  // forced those parents to full EditLayoutModal while dependent picklists still quick-edited.
  if (field.is_path_field === true && !isPicklist) return false;
  const unsupported = ['calculated', 'rollupsummary', 'image', 'barcode', 'qrcode'];
  if (unsupported.includes(t)) return false;
  return true;
}
